import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import EcoIcon from "../../../components/eco-icon/EcoIcon";
import EcoPageHeader from "../../../components/eco-page-header/EcoPageHeader";
import EcoPagination from "../../../components/eco-ui/EcoPagination";
import EcoSegmented from "../../../components/eco-ui/EcoSegmented";
import type { RootState } from "../../../store/store";

import {
  ASO_TYPE_LABELS,
  DOCUMENT_CATEGORY_LABELS,
  documentTypeName,
  DpDocumentCategory,
  DpExpiringDocument,
  listExpiringDocumentsApi,
} from "../api/documentsApi";
import { listCostCentersApi, DpCostCenter } from "../api/settingsApi";
import DocumentExpiryBadge from "../components/DocumentExpiryBadge";
import { formatDateOnly } from "../utils/format";
import { DP_EMPLOYEE_DOCUMENT_PAGE, DP_PAGE, DP_PROCESSES_PAGE } from "../routes";
import { useGoBack } from "../../../hooks/useGoBack";
import "./Dp.css";
import "./DpEco.css";

const LIMIT = 20;
const WINDOWS = [30, 60, 90];

/**
 * Documentos vencidos ou que vencem nos próximos N dias, de todos os colaboradores
 * não desligados, em ordem de vencimento. Só os vigentes (substituídos não entram).
 */
export default function ExpiringDocumentsPage() {
  const navigate = useNavigate();
  const goBack = useGoBack(DP_PAGE());
  const token = useSelector((state: RootState) => state.auth.accessToken);

  const [days, setDays] = useState(60);
  const [category, setCategory] = useState<DpDocumentCategory | "">("");
  const [costCenterId, setCostCenterId] = useState("");
  const [costCenters, setCostCenters] = useState<DpCostCenter[]>([]);
  const [page, setPage] = useState(1);

  const [items, setItems] = useState<DpExpiringDocument[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token) return;
    listCostCentersApi(token)
      .then(setCostCenters)
      .catch(() => setCostCenters([]));
  }, [token]);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const result = await listExpiringDocumentsApi(token, {
        page,
        limit: LIMIT,
        days,
        category: category || undefined,
        costCenterId: costCenterId || undefined,
      });
      setItems(result.items);
      setTotal(result.total);
    } catch (error: any) {
      toast.error(error?.message || "Erro ao carregar os vencimentos.");
    } finally {
      setLoading(false);
    }
  }, [token, page, days, category, costCenterId]);

  useEffect(() => {
    load();
  }, [load]);

  if (!token) return null;

  const windowOptions = WINDOWS.map((w) => ({ value: String(w), label: `${w} dias` }));
  const openDocument = (d: DpExpiringDocument) => navigate(DP_EMPLOYEE_DOCUMENT_PAGE(d.employee.id, d.id));
  const documentLabel = (d: DpExpiringDocument) =>
    `${documentTypeName(d)}${d.asoType ? ` · ${ASO_TYPE_LABELS[d.asoType]}` : ""}`;

  return (
    <div className="dpLayout">
      <main className="dpMain">
        <div className="dpContainer">
          <EcoPageHeader
            crumbs={[
              { label: "Departamento Pessoal", onClick: () => navigate(DP_PAGE()) },
              { label: "Documentos a vencer" },
            ]}
            title="Documentos a vencer"
            subtitle="Vencidos e os que vencem na janela escolhida: ASO, NRs e demais documentos com validade."
            onBack={goBack}
            backLabel="Voltar para o DP"
            actions={
              <button type="button" className="ecoBtn" onClick={() => navigate(DP_PROCESSES_PAGE())}>
                <EcoIcon name="checkSquare" size={18} />
                Admissão e desligamento
              </button>
            }
          />

          <section className="ecoCard dpeList" aria-label="Documentos">
            <div className="dpeList__toolbar">
              <div className="dpeList__filters">
                <div className="dpeWindow">
                  <span className="dpeWindow__label">Vencidos e próximos</span>
                  <EcoSegmented
                    label="Janela de vencimento"
                    options={windowOptions}
                    value={String(days)}
                    onChange={(v) => {
                      setPage(1);
                      setDays(Number(v));
                    }}
                  />
                </div>
                <label htmlFor="doc-cat" className="ecoVisuallyHidden">Categoria</label>
                <select
                  id="doc-cat"
                  className="ecoSelect ecoSelect--sm dpeList__select"
                  value={category}
                  onChange={(e) => {
                    setPage(1);
                    setCategory(e.target.value as DpDocumentCategory | "");
                  }}
                >
                  <option value="">Todas as categorias</option>
                  {(Object.keys(DOCUMENT_CATEGORY_LABELS) as DpDocumentCategory[]).map((c) => (
                    <option key={c} value={c}>
                      {DOCUMENT_CATEGORY_LABELS[c]}
                    </option>
                  ))}
                </select>
                <label htmlFor="doc-cc" className="ecoVisuallyHidden">Centro de custo</label>
                <select
                  id="doc-cc"
                  className="ecoSelect ecoSelect--sm dpeList__select"
                  value={costCenterId}
                  onChange={(e) => {
                    setPage(1);
                    setCostCenterId(e.target.value);
                  }}
                >
                  <option value="">Todos os centros de custo</option>
                  {costCenters.map((cc) => (
                    <option key={cc.id} value={cc.id}>
                      {cc.code} — {cc.name}
                    </option>
                  ))}
                </select>
              </div>
              <span className="dpeList__count" aria-live="polite">
                {loading ? "Carregando..." : `${total} ${total === 1 ? "documento" : "documentos"}`}
              </span>
            </div>

            {!loading && items.length === 0 ? (
              <div className="dpeList__empty">
                <p className="ecoEmpty">Nenhum documento vencido ou a vencer nesta janela.</p>
              </div>
            ) : (
              <>
                <div className="ecoTableWrap dpeList__table">
                  <table className="ecoTable">
                    <thead>
                      <tr>
                        <th scope="col">Colaborador</th>
                        <th scope="col">Documento</th>
                        <th scope="col">Validade</th>
                        <th scope="col">Situação</th>
                        <th scope="col" className="ecoTable__num">Ação</th>
                      </tr>
                    </thead>
                    <tbody aria-busy={loading}>
                      {items.map((d) => (
                        <tr key={d.id}>
                          <td className="dpePerson__name">{d.employee.fullName}</td>
                          <td>
                            <span className="dpePerson__text">
                              <span>{documentLabel(d)}</span>
                              <span className="dpePerson__sub">
                                {d.documentType ? DOCUMENT_CATEGORY_LABELS[d.documentType.category] : "Sem tipo"}
                                {d.title ? ` · ${d.title}` : ""}
                              </span>
                            </span>
                          </td>
                          <td className="ecoTable__muted">{formatDateOnly(d.expiresAt)}</td>
                          <td>
                            <DocumentExpiryBadge status={d.expiryStatus} daysToExpire={d.daysToExpire} />
                          </td>
                          <td className="ecoTable__num">
                            <button
                              type="button"
                              className="ecoBtn ecoBtn--sm"
                              onClick={() => openDocument(d)}
                              aria-label={`Abrir ${documentLabel(d)} de ${d.employee.fullName} na ficha`}
                            >
                              Abrir na ficha
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <ul className="dpeList__cards" aria-busy={loading}>
                  {items.map((d) => (
                    <li key={d.id}>
                      <button type="button" className="dpeList__card" onClick={() => openDocument(d)}>
                        <span className="dpePerson__text" style={{ flex: 1 }}>
                          <span className="dpePerson__name">{d.employee.fullName}</span>
                          <span className="dpePerson__sub">
                            {documentLabel(d)} · vence {formatDateOnly(d.expiresAt)}
                          </span>
                        </span>
                        <DocumentExpiryBadge status={d.expiryStatus} daysToExpire={d.daysToExpire} />
                        <EcoIcon name="chevronRight" className="dpeList__cardChevron" />
                      </button>
                    </li>
                  ))}
                </ul>
              </>
            )}

            <div className="dpeList__pager">
              <EcoPagination page={page} pageSize={LIMIT} total={total} onChange={setPage} disabled={loading} />
            </div>
          </section>
        </div>
      </main>
    </div>
  );
}
